import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  fields: [],
  fieldsLoading: false,
  fieldsError: null,

  activityData: null,
  fetchActivityLoading: false,
  fetchActivityError: null,

  // Writes are asynchronous: the POST only acknowledges the command. These track
  // the wait for the projected activity to become readable.
  createActivity: {
    loading: false,
    error: null,
    fieldErrors: [],
    result: null,
    pendingActivityId: null,
    readyActivityId: null,
  },
  updateActivity: {
    loading: false,
    error: null,
    fieldErrors: [],
    result: null,
    pendingActivityId: null,
    readyActivityId: null,
  },
};

const activityFormSlice = createSlice({
  name: "activityForm",
  initialState,
  reducers: {
    fetchActivityFieldsRequest: (state) => {
      state.fieldsLoading = true;
      state.fieldsError = null;
    },
    fetchActivityFieldsSuccess: (state, action) => {
      state.fieldsLoading = false;
      state.fields = action.payload;
    },
    fetchActivityFieldsError: (state, action) => {
      state.fieldsLoading = false;
      state.fieldsError = action.payload;
    },
    clearActivityFields: (state) => {
      state.fields = [];
      state.fieldsLoading = false;
      state.fieldsError = null;
    },

    fetchActivityByIdRequest: (state) => {
      state.fetchActivityLoading = true;
      state.fetchActivityError = null;
    },
    fetchActivityByIdSuccess: (state, action) => {
      state.fetchActivityLoading = false;
      state.activityData = action.payload;
    },
    fetchActivityByIdError: (state, action) => {
      state.fetchActivityLoading = false;
      state.fetchActivityError = action.payload;
    },
    clearActivityData: (state) => {
      state.activityData = null;
      state.fetchActivityLoading = false;
      state.fetchActivityError = null;
    },

    createActivityRequest: (state) => {
      state.createActivity.loading = true;
      state.createActivity.error = null;
      state.createActivity.fieldErrors = [];
      state.createActivity.result = null;
      state.createActivity.pendingActivityId = null;
      state.createActivity.readyActivityId = null;
    },
    createActivitySuccess: (state, action) => {
      state.createActivity.loading = false;
      state.createActivity.result = action.payload;
    },
    // The gateway accepted the command. Loading deliberately stays true: the
    // activity is not readable yet, and redirecting now lands on a claim page
    // whose activity list does not contain it.
    createActivityAccepted: (state, action) => {
      state.createActivity.pendingActivityId = action.payload?.activityId ?? null;
    },
    // The projection exists -- either the socket said so, or the poll found it.
    createActivityReady: (state, action) => {
      state.createActivity.loading = false;
      state.createActivity.readyActivityId = action.payload?.activityId ?? null;
    },
    createActivityError: (state, action) => {
      state.createActivity.loading = false;
      const payload = action.payload;
      if (payload && typeof payload === 'object') {
        state.createActivity.error = payload.message ?? null;
        state.createActivity.fieldErrors = payload.fields ?? [];
      } else {
        state.createActivity.error = payload;
        state.createActivity.fieldErrors = [];
      }
    },
    resetCreateActivity: (state) => {
      state.createActivity = { ...initialState.createActivity };
    },

    updateActivityRequest: (state) => {
      state.updateActivity.loading = true;
      state.updateActivity.error = null;
      state.updateActivity.fieldErrors = [];
      state.updateActivity.result = null;
      state.updateActivity.pendingActivityId = null;
      state.updateActivity.readyActivityId = null;
    },
    updateActivitySuccess: (state, action) => {
      state.updateActivity.loading = false;
      state.updateActivity.result = action.payload;
    },
    updateActivityAccepted: (state, action) => {
      state.updateActivity.pendingActivityId = action.payload?.activityId ?? null;
    },
    updateActivityReady: (state, action) => {
      state.updateActivity.loading = false;
      state.updateActivity.readyActivityId = action.payload?.activityId ?? null;
    },
    updateActivityError: (state, action) => {
      state.updateActivity.loading = false;
      const payload = action.payload;
      if (payload && typeof payload === 'object') {
        state.updateActivity.error = payload.message ?? null;
        state.updateActivity.fieldErrors = payload.fields ?? [];
      } else {
        state.updateActivity.error = payload;
        state.updateActivity.fieldErrors = [];
      }
    },
    resetUpdateActivity: (state) => {
      state.updateActivity = { ...initialState.updateActivity };
    },

    resetActivityForm: () => initialState,
  },
});

export const {
  fetchActivityFieldsRequest,
  fetchActivityFieldsSuccess,
  fetchActivityFieldsError,
  clearActivityFields,
  fetchActivityByIdRequest,
  fetchActivityByIdSuccess,
  fetchActivityByIdError,
  clearActivityData,
  createActivityRequest,
  createActivitySuccess,
  createActivityAccepted,
  createActivityReady,
  createActivityError,
  resetCreateActivity,
  updateActivityRequest,
  updateActivitySuccess,
  updateActivityAccepted,
  updateActivityReady,
  updateActivityError,
  resetUpdateActivity,
  resetActivityForm,
} = activityFormSlice.actions;

export default activityFormSlice.reducer;
